import { hasPassengersOnBoard } from '@backoffice-frontend/ad-passenger-list'
import { useRemoteOperationsAreaOperatorRole } from '@backoffice-frontend/ad-remote-operations-area'
import {
  FccAdAlertType,
  FccOperatorRoleType,
} from '@backoffice-frontend/graphql'
import type {
  UseAlertsForRoleAdAlertFragment,
  UseAlertsForRoleVehicleFragment,
} from './useAlertsForRole.hook'

// Tasks that only the Rider Support or Remote Assistance area handles, so the
// AD fleet map and other surfaces without a role don't show them.
const alertsExcludedFromAdFleetMap: FccAdAlertType[] = [
  FccAdAlertType.CabinDeboarding,
]

/**
 * Whether this alert is handled by the given role. Without a role, as on the
 * AD fleet map, every alert is shown except `alertsExcludedFromAdFleetMap`.
 * Without passengers on board, the RAO handles the emergency stop alone, so
 * rider support doesn't see it. Once the RAO cleared the vehicle, it waits for
 * rider support, so rider support keeps the stop even if the passengers leave.
 *
 * Prefer useAlertsForRole; reach for this predicate only where a hook cannot be
 * called, such as inside a per-vehicle callback.
 */
export const isAlertVisibleToRole = (
  alert: UseAlertsForRoleAdAlertFragment,
  role: FccOperatorRoleType | undefined,
  vehicle: UseAlertsForRoleVehicleFragment,
): boolean => {
  if (!role) {
    return !alertsExcludedFromAdFleetMap.includes(alert.type)
  }
  if (!alert.visibleTo.includes(role)) {
    return false
  }
  return !(
    role === FccOperatorRoleType.RiderSupport &&
    alert.type === FccAdAlertType.EmergencyStop &&
    !vehicle.fccIsVehicleCleared &&
    !hasPassengersOnBoard(vehicle)
  )
}

/**
 * The alerts of the vehicle visible to the current area's operator role, in the order the
 * backend returned them.
 */
export const useAlertsForRole = <A extends UseAlertsForRoleAdAlertFragment>(
  alerts: A[] | undefined,
  vehicle: UseAlertsForRoleVehicleFragment,
): A[] => {
  const role = useRemoteOperationsAreaOperatorRole()

  return (alerts ?? []).filter(alert =>
    isAlertVisibleToRole(alert, role, vehicle),
  )
}
